/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

export const PROVIDERS = {
  aws: "AWS",
  azure: "AZURE",
  gcp: "GCP",
  ibmcloud: "IBMCLOUD",
  hybrid: "HYBRID",
} as const;

export const HYBRID_PROVIDERS = {
  vmware: "VMWARE",
  kubernetes: "KUBERNETES",
  openShift: "OPENSHIFT",
} as const;

export type ProviderKeys = (typeof PROVIDERS)[keyof typeof PROVIDERS];

export type HybridProviderTypes =
  (typeof HYBRID_PROVIDERS)[keyof typeof HYBRID_PROVIDERS];

export const AZURE_COMMAND = `az ad sp create-for-rbac --name "nordcloud-IBM Multicloud Accelerator" --skip-assignment`;

export const URL_AWS_CONSOLE = "https://console.aws.amazon.com/console/home";
export const URL_AWS_TEMPLATE =
  "https://nordcloud-pat-public-assets.s3-eu-west-1.amazonaws.com/templates/IBM Multicloud Accelerator/IBM Multicloud AcceleratorAWSScannerServiceRole.yml";
export const URL_AWS_EXTERNAL_IDS =
  "https://aws.amazon.com/blogs/security/how-to-use-external-id-when-granting-access-to-your-aws-resources/";

export const URL_AZURE = "https://portal.azure.com/";
export const URL_AZURE_SUBSCRIPTION_ID =
  "https://docs.microsoft.com/en-us/azure/media-services/latest/setup-azure-subscription-how-to?tabs=portal";

export const URL_IBMCLOUD = "https://cloud.ibm.com/";
export const URL_IBMCLOUD_ACCOUNT_OVERVIEW =
  "https://cloud.ibm.com/iam/overview";
export const URL_IBMCLOUD_IAM_USER = "https://cloud.ibm.com/iam/users";
export const URL_IBMCLOUD_NOTICATION = "https://cloud.ibm.com/notifications";
export const URL_IBMCLOUD_CREATE_API_KEY = "https://cloud.ibm.com/iam/apikeys";

export const URL_GCP_CONSOLE = "https://console.cloud.google.com/";
export const URL_GCP_STORAGE =
  "https://console.cloud.google.com/storage/browser";
export const URL_GCP_SERVICE_ACCOUNTS =
  "https://console.cloud.google.com/iam-admin/serviceaccounts";
export const URL_GCP_IAM =
  "https://console.cloud.google.com/iam-admin/serviceaccounts";
export const URL_GCP_IAM_CREDENTIALS =
  "https://console.cloud.google.com/marketplace/product/google/iamcredentials.googleapis.com";

export const AWS_INFO_ONBOARDING = `
For on-boarding process, each account needs to have an IAM role
created with read only permissions and trust relationship set to
Nordcloud’s AWS account. For your convenience, please use provided
CloudFormation template. There is no requirement for deployment
region, but we recommend eu-west-1.`;

export const AWS_INFO_EXTERNAL_ID = `AWS External ID should be a randomly generated long string of
different characters. It will be working as a password between
IBM Multicloud Accelerator and AWS Account.`;

type FormLabels = {
  titleInfo?: string;
  firstStepInfo?: string;
  clusterLabel?: string;
  clusterOwner?: string;
  secondStepInfo?: string;
  ownerError?: string;
  instructions?: string;
};

export const VMWARE_TEXT: FormLabels = {
  titleInfo: `You can onboard to IBM Multicloud Accelerator your VMWare and map your resources to the proper applications and environments.`,
  firstStepInfo: `Give IBM Multicloud Accelerator the name of your vmware, the vmware owner and create the vmware.`,
  clusterLabel: `VMWare Name`,
  clusterOwner: `VMWare Owner`,
  secondStepInfo: `Setting the scanner inside the VMWare cluster.`,
  ownerError: `VMWare owner is required.`,
  instructions: `Follow the steps and add your VMWare to IBM Multicloud Accelerator. When the VM will be added, it will be visible on the list of all your accounts, under the VMWare tab. Go to the details of your newly added cluster in IBM Multicloud Accelerator and download the Deployment JSON file, visible in the top right corner. This file needs to be placed in your VM to send the data to IBM Multicloud Accelerator. After placing the Deployment JSON into your VM cluster it might take up to 30 minutes for IBM Multicloud Accelerator to highlight your VM with the green icon and get first data.`,
};

export const KUBERNETES_TEXT: FormLabels = {
  titleInfo: `You can onboard to IBM Multicloud Accelerator your Kubernetes clusters and map your resources to the proper applications and environments.
Here we prepared easy two-step onboarding process where you can create your cluster to be visible in IBM Multicloud Accelerator as a first step, 
and after that you deploy simple .yaml file inside your cluster to feed IBM Multicloud Accelerator with the data.`,
  firstStepInfo: `Give IBM Multicloud Accelerator the name of your cluster, the cluster owner and create the cluster.`,
  clusterLabel: `Cluster Name`,
  clusterOwner: `Cluster Owner`,
  secondStepInfo: `Setting the scanner inside the Kubernetes cluster.`,
  ownerError: `Cluster owner is required.`,
  instructions: `Follow the steps and add your Kubernetes cluster to IBM Multicloud Accelerator. When the cluster will be added, it will be visible on the list of all your accounts, under the Kubernetes tab. Go to the details of your newly added cluster in IBM Multicloud Accelerator and download the Deployment YAML file, visible in the top right corner. This file needs to be placed in your cluster to send the data to IBM Multicloud Accelerator. After placing the Deployment YAML into your Kubernetes cluster it might take up to 30 minutes for IBM Multicloud Accelerator to highlight your cluster with the green icon and get first data.`,
};

export const REQUIRED_VALIDATION_MESSAGE = "This field is required.";
